import { useCallback, useEffect, useRef, useState } from 'react'
import { useMyTeamStore } from '../store/my-team.store'
import { UsedSkillMetrics } from '../model/my-team-skill-gaps'

const PAGE_SIZE = 10
const DEBOUNCE_DELAY = 500

export const useSkillSelection = (skillName?: string) => {
  const myTeamStore = useMyTeamStore()

  const [selectedSkills, setSelectedSkills] = useState<UsedSkillMetrics[]>([])
  const [allSkills, setAllSkills] = useState<UsedSkillMetrics[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [skip, setSkip] = useState(0)
  const [searchText, setSearchText] = useState(skillName || '')
  const [inputValue, setInputValue] = useState(skillName || '')
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const prevSearchTextRef = useRef('')
  const debounceTimerRef = useRef<NodeJS.Timeout>()
  const hasAutoSelectedRef = useRef(false)

  const { isLoading: isLoadingSkills } = myTeamStore.getGenericFieldsState('getAllUsedSkills')
  const hasMore = totalCount > allSkills.length

  // Cleanup debounce timer
  useEffect(() => () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
  }, [])

  // Reset skip when search changes
  useEffect(() => {
    if (prevSearchTextRef.current !== searchText) {
      setSkip(PAGE_SIZE)
      prevSearchTextRef.current = searchText
    }
  }, [searchText])

  // Fetch skills
  useEffect(() => {
    const fetchSkills = async () => {
      await myTeamStore.getAllUsedSkills(0, PAGE_SIZE, searchText, 'asc')

      setAllSkills(myTeamStore.allUsedSkills.skillSets)
      setTotalCount(myTeamStore.allUsedSkills.count)
      setSkip(PAGE_SIZE)
      setIsInitialLoad(false)
    }

    fetchSkills()
  }, [searchText, myTeamStore])

  // Auto-select skill when skillName prop is provided
  useEffect(() => {
    if (skillName && allSkills.length > 0 && selectedSkills.length === 0 && !isInitialLoad && !hasAutoSelectedRef.current) {
      const matchingSkill = allSkills.find(skill => skill.setName.toLowerCase() === skillName.toLowerCase())

      if (matchingSkill) {
        setSelectedSkills([matchingSkill])
        const skillIds = matchingSkill.ids
        myTeamStore.getSkillGapInsights(skillIds, null, null)
        hasAutoSelectedRef.current = true
      }
    }
  }, [skillName, allSkills, selectedSkills.length, isInitialLoad, myTeamStore])

  // Reset auto-selection flag when skillName changes
  useEffect(() => {
    hasAutoSelectedRef.current = false
  }, [skillName])

  const handleLoadMore = useCallback(() => {
    myTeamStore.getAllUsedSkills(skip, PAGE_SIZE, searchText, 'asc').then(() => {
      setAllSkills(prev => [...prev, ...myTeamStore.allUsedSkills.skillSets])
      setSkip(prev => prev + PAGE_SIZE)
    })
  }, [skip, searchText, myTeamStore])

  const handleInputChange = useCallback((_: any, value: string) => {
    setInputValue(value)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchText(value)
    }, DEBOUNCE_DELAY)
  }, [])

  return {
    selectedSkills,
    setSelectedSkills,
    allSkills,
    totalCount,
    hasMore,
    inputValue,
    isLoadingSkills,
    isInitialLoad,
    handleLoadMore,
    handleInputChange,
  }
}
